import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    IndianRupee, TrendingUp, TrendingDown, AlertCircle,
    CheckCircle2, Clock, X, Plus, Trash2, Edit2,
    Download, Search, Filter, RefreshCw, FileText,
    Receipt, CreditCard, Wallet, Building2, Users,
    BarChart3, PieChart as PieIcon, ArrowUpRight,
    ArrowDownRight, ChevronRight, AlertTriangle,
    Banknote, BookOpen, Send, Eye, Check, XCircle,
    CalendarDays, Briefcase, HardHat
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell,
    AreaChart, Area, LineChart, Line, Legend
} from 'recharts';
import { api, API_BASE } from '../api';
import { useServerEvents } from '../useServerEvents';

// ── Styles ────────────────────────────────────────────────────
const S = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Outfit:wght@600;700;800&display=swap');
:root {
  --bg:#f8fafc; --card:#fff; --border:#e2e8f0;
  --blue:#1743e6; --blue-light:#eef3ff; --blue-border:#c9d7ff;
  --green:#16a34a; --green-light:#f0fdf4; --green-border:#bbf7d0;
  --red:#dc2626; --red-light:#fef2f2; --red-border:#fecaca;
  --amber:#d97706; --amber-light:#fffbeb; --amber-border:#fde68a;
  --text:#0f172a; --muted:#64748b; --subtle:#94a3b8;
  --sh:0 1px 3px rgba(0,0,0,0.06); --shd:0 4px 14px rgba(0,0,0,0.09);
  --shl:0 12px 32px rgba(0,0,0,0.14);
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
.fin-root{display:flex;height:100%;overflow:hidden;font-family:'Plus Jakarta Sans',sans-serif;background:var(--bg);color:var(--text);-webkit-font-smoothing:antialiased;}

/* Sidebar */
.fin-sb{width:220px;background:linear-gradient(180deg,#1130c2 0%,#1743e6 60%,#1c4bf2 100%);flex-shrink:0;display:flex;flex-direction:column;padding:18px 10px;overflow-y:auto;}
.fin-sb::-webkit-scrollbar{display:none;}
.fin-logo{display:flex;align-items:center;gap:8px;padding:0 6px;margin-bottom:20px;}
.fin-logo-ico{width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#1743e6,#2f5bf0);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.fin-logo-txt{font-size:13px;font-weight:800;color:#fff;letter-spacing:-0.2px;line-height:1.2;}
.fin-logo-sub{font-size:9px;color:rgba(255,255,255,0.3);letter-spacing:0.3px;}
.fin-nav-sec{font-size:9px;font-weight:700;color:rgba(255,255,255,0.25);text-transform:uppercase;letter-spacing:1px;padding:0 6px;margin:14px 0 5px;}
.fin-ni{display:flex;align-items:center;gap:8px;padding:9px 10px;border-radius:7px;border:none;background:transparent;color:rgba(255,255,255,0.4);cursor:pointer;font-size:12px;font-weight:500;font-family:'Plus Jakarta Sans',sans-serif;transition:all 0.12s;width:100%;text-align:left;}
.fin-ni:hover{background:rgba(255,255,255,0.06);color:rgba(255,255,255,0.8);}
.fin-ni.active{background:rgba(59,130,246,0.18);color:#c9d7ff;font-weight:700;border-left:2px solid #2f5bf0;padding-left:8px;}
.fin-ni .badge{margin-left:auto;background:#ef4444;color:#fff;border-radius:8px;font-size:9px;font-weight:800;padding:1px 5px;}
.fin-user{margin-top:auto;padding-top:12px;border-top:1px solid rgba(255,255,255,0.07);display:flex;align-items:center;gap:8px;padding:12px 6px 0;}
.fin-av{width:30px;height:30px;border-radius:8px;background:linear-gradient(135deg,#0891b2,#06b6d4);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;flex-shrink:0;}
.fin-uname{font-size:12px;font-weight:700;color:#fff;}
.fin-urole{font-size:10px;color:rgba(255,255,255,0.3);}

/* Main */
.fin-main{flex:1;overflow-y:auto;background:var(--bg);padding:28px 32px;}
.fin-main::-webkit-scrollbar{width:4px;}
.fin-main::-webkit-scrollbar-thumb{background:var(--border);border-radius:4px;}
.fin-hdr{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid var(--border);}
.fin-ttl{font-family:'Outfit',sans-serif;font-size:22px;font-weight:800;letter-spacing:-0.5px;}
.fin-sub{font-size:12px;color:var(--muted);margin-top:3px;}

/* KPI grid */
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px;}
.kpi{background:var(--card);border:1px solid var(--border);border-radius:13px;padding:18px 20px;box-shadow:var(--sh);position:relative;overflow:hidden;cursor:pointer;transition:all 0.15s;}
.kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--kpi-color,var(--blue));border-radius:13px 13px 0 0;}
.kpi:hover{box-shadow:var(--shd);transform:translateY(-2px);}
.kpi-ico{width:38px;height:38px;border-radius:9px;display:flex;align-items:center;justify-content:center;margin-bottom:12px;}
.kpi-lbl{font-size:10px;font-weight:700;color:var(--subtle);text-transform:uppercase;letter-spacing:0.6px;}
.kpi-val{font-family:'Outfit',sans-serif;font-size:26px;font-weight:800;line-height:1;margin:4px 0 3px;letter-spacing:-0.5px;}
.kpi-sub{font-size:11px;color:var(--muted);}
.kpi-trend{position:absolute;top:16px;right:16px;display:flex;align-items:center;gap:3px;font-size:11px;font-weight:700;padding:3px 7px;border-radius:5px;}

/* Cards */
.card{background:var(--card);border:1px solid var(--border);border-radius:13px;padding:20px;box-shadow:var(--sh);}
.card-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;}
.card-ttl{font-size:14px;font-weight:700;letter-spacing:-0.2px;}

/* Tables */
.ftbl{width:100%;border-collapse:collapse;}
.ftbl th{text-align:left;padding:9px 12px;font-size:10px;font-weight:700;color:var(--subtle);background:#f8fafc;border-bottom:1px solid var(--border);text-transform:uppercase;letter-spacing:0.5px;}
.ftbl th:first-child{border-radius:6px 0 0 6px;}
.ftbl th:last-child{border-radius:0 6px 6px 0;}
.ftbl td{padding:11px 12px;border-bottom:1px solid #f8fafc;font-size:13px;color:#334155;}
.ftbl tr:last-child td{border-bottom:none;}
.ftbl tr.cr{cursor:pointer;transition:background 0.1s;}
.ftbl tr.cr:hover td{background:#f8fafc;}

/* Badges */
.badge{padding:3px 8px;border-radius:4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.3px;}
.b-paid{background:var(--green-light);color:var(--green);}
.b-unpaid{background:var(--amber-light);color:var(--amber);}
.b-overdue{background:var(--red-light);color:var(--red);}
.b-pending{background:var(--amber-light);color:var(--amber);}
.b-approved{background:var(--green-light);color:var(--green);}
.b-rejected{background:var(--red-light);color:var(--red);}
.b-cancelled{background:#f1f5f9;color:var(--muted);}
.b-partial{background:#e0e7ff;color:#3730a3;}

/* Buttons */
.btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;font-weight:600;cursor:pointer;font-size:12px;font-family:'Plus Jakarta Sans',sans-serif;border:none;transition:all 0.13s;white-space:nowrap;}
.btn-primary{background:var(--blue);color:#fff;box-shadow:0 2px 6px rgba(29,78,216,0.25);}
.btn-primary:hover{background:#0f2ea8;box-shadow:0 4px 12px rgba(29,78,216,0.35);transform:translateY(-1px);}
.btn-ghost{background:var(--card);border:1px solid var(--border);color:var(--text);}
.btn-ghost:hover{background:#f8fafc;}
.btn-green{background:var(--green-light);color:var(--green);border:1px solid var(--green-border);}
.btn-green:hover{background:var(--green-border);}
.btn-red{background:var(--red-light);color:var(--red);border:1px solid var(--red-border);}
.btn-red:hover{background:var(--red-border);}
.btn-amber{background:var(--amber-light);color:var(--amber);border:1px solid var(--amber-border);}

/* Search */
.sw{position:relative;}.sw svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--subtle);pointer-events:none;}
.si{padding:8px 12px 8px 30px;border:1px solid var(--border);border-radius:8px;font-size:13px;outline:none;width:200px;font-family:'Plus Jakarta Sans',sans-serif;background:var(--card);}
.si:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-light);}

/* Modal */
.moverlay{position:fixed;inset:0;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;z-index:1000;backdrop-filter:blur(6px);animation:fadeIn 0.16s;}
.mbox{background:var(--card);width:100%;max-width:560px;border-radius:16px;box-shadow:var(--shl);max-height:92vh;overflow-y:auto;animation:slideUp 0.2s;border:1px solid var(--border);}
.mhdr{display:flex;justify-content:space-between;align-items:center;padding:20px 24px 16px;border-bottom:1px solid #f1f5f9;}
.mhdr h2{margin:0;font-size:15px;font-weight:800;letter-spacing:-0.3px;}
.mbody{padding:20px 24px 24px;}
.mcls{background:#f1f5f9;border:none;cursor:pointer;padding:5px;border-radius:7px;display:flex;color:var(--muted);}
.mcls:hover{background:var(--border);color:var(--text);}
.fg{margin-bottom:13px;}
.fg label{display:block;font-size:11px;font-weight:700;margin-bottom:4px;color:#374151;text-transform:uppercase;letter-spacing:0.4px;}
.fg input,.fg select,.fg textarea{width:100%;padding:9px 11px;border:1.5px solid var(--border);border-radius:8px;font-size:13px;outline:none;font-family:'Plus Jakarta Sans',sans-serif;background:var(--card);color:var(--text);transition:border-color 0.12s;}
.fg input:focus,.fg select:focus,.fg textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-light);}
.fg textarea{resize:vertical;min-height:70px;line-height:1.5;}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;}

/* Progress bar */
.pb{height:5px;background:#f1f5f9;border-radius:3px;overflow:hidden;margin-top:5px;}
.pf{height:100%;border-radius:3px;transition:width 0.5s ease;}

/* Misc */
.divider{height:1px;background:var(--border);margin:16px 0;}
.empty{text-align:center;padding:48px 20px;color:var(--subtle);}
.empty svg{margin:0 auto 10px;display:block;opacity:0.3;}
.loading{display:flex;align-items:center;justify-content:center;padding:60px;gap:10px;color:var(--muted);}
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin 0.8s linear infinite;}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}

/* Summary cards */
.sum-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;}
.alert-banner{display:flex;align-items:flex-start;gap:9px;padding:11px 14px;border-radius:9px;font-size:12px;font-weight:500;margin-bottom:14px;border:1px solid;}
.ab-warn{background:var(--amber-light);border-color:var(--amber-border);color:var(--amber);}
.ab-red{background:var(--red-light);border-color:var(--red-border);color:var(--red);}
`;

const fmt = (n) => {
    if (!n || isNaN(n)) return '₹0';
    const num = parseFloat(n);
    if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
    if (num >= 1000)   return `₹${(num / 1000).toFixed(1)}K`;
    return `₹${num.toFixed(0)}`;
};

const PIE_COLORS = ['#1743e6','#10b981','#f59e0b','#ef4444','#8b5cf6','#0891b2','#ec4899'];

const NAV = [
    { id:'Dashboard',   icon:BarChart3,  section:'overview' },
    { id:'Invoices',    icon:FileText,   section:'invoices' },
    { id:'Payments',    icon:CreditCard, section:'payments' },
    { id:'Expenses',    icon:Receipt,    section:'expenses' },
    { id:'Budget',      icon:Wallet,     section:'budget'   },
    { id:'Vendor Costs',icon:Building2,  section:'vendors'  },
    { id:'Labour Cost', icon:HardHat,    section:'labour'   },
    { id:'Finance Team',icon:Users,      section:'team'     },
];

export default function FinanceDashboard({ user }) {
    const [tab,       setTab]       = useState('Dashboard');
    const [loading,   setLoading]   = useState(true);
    const [search,    setSearch]    = useState('');
    const [toast,     setToast]     = useState(null);
    const [modal,     setModal]     = useState(null);
    const [editItem,  setEditItem]  = useState(null);
    const [saving,    setSaving]    = useState(false);

    const [summary,   setSummary]   = useState(null);
    const [invoices,  setInvoices]  = useState([]);
    const [payments,  setPayments]  = useState([]);
    const [expenses,  setExpenses]  = useState([]);
    const [budget,    setBudget]    = useState([]);
    const [finTeam,   setFinTeam]   = useState([]);
    const [projects,  setProjects]  = useState([]);
    const [vendors,   setVendors]   = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [labor,     setLabor]     = useState([]);
    const [indents,   setIndents]   = useState([]);

    const showToast = useCallback((msg) => {
        setToast(msg); setTimeout(() => setToast(null), 3200);
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [sum, inv, pay, exp, bud, team, proj, ven, eng, lab, ind] = await Promise.all([
                api('finance/summary'),
                api('finance/invoices'),
                api('finance/payments'),
                api('finance/expenses'),
                api('finance/budget'),
                api('finance/team'),
                api('projects'),
                api('vendors'),
                api('engineers'),
                api('labor'),
                api('indents'),
            ]);
            setSummary(sum || {});
            setInvoices(Array.isArray(inv) ? inv : []);
            setPayments(Array.isArray(pay) ? pay : []);
            setExpenses(Array.isArray(exp) ? exp : []);
            setBudget(Array.isArray(bud)   ? bud : []);
            setFinTeam(Array.isArray(team) ? team : []);
            setProjects(Array.isArray(proj)? proj : []);
            setVendors(Array.isArray(ven)  ? ven  : []);
            setEngineers(Array.isArray(eng)? eng  : []);
            setLabor(Array.isArray(lab)    ? lab  : []);
            setIndents(Array.isArray(ind)  ? ind  : []);
            lastOkRef.current = Date.now();
            setLiveStatus('live');
        } catch (err) {
            setLiveStatus('offline');
            showToast('⚠ Could not load finance data.');
        }
        setLoading(false);
    }, [showToast]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    useEffect(() => {
        const off = () => setLiveStatus('offline');
        const on = () => { setLiveStatus('connecting'); fetchAll(); };
        window.addEventListener('offline', off); window.addEventListener('online', on);
        const hb = setInterval(async () => {
            if (!navigator.onLine) { setLiveStatus('offline'); return; }
            try {
                const c = new AbortController(); const t = setTimeout(() => c.abort(), 4000);
                const r = await fetch(`${API_BASE}/health`, { signal: c.signal, cache: 'no-store' });
                clearTimeout(t);
                if (r.ok) { lastOkRef.current = Date.now(); setLiveStatus('live'); }
            } catch { if (Date.now() - lastOkRef.current > 8000) setLiveStatus('offline'); }
        }, 5000);
        return () => { window.removeEventListener('offline', off); window.removeEventListener('online', on); clearInterval(hb); };
    }, [fetchAll]);

    const [liveStatus, setLiveStatus] = useState(navigator.onLine ? 'connecting' : 'offline');
    const lastOkRef = useRef(0);

    useServerEvents({
        onConnected: useCallback(() => setLiveStatus('live'),    []),
        onError:     useCallback(() => { fetchAll(); }, [fetchAll]),
        onInit: useCallback((data) => {
            // Finance gets relevant slices from the shared SSE stream
            if (data.projects)  setProjects(Array.isArray(data.projects)  ? data.projects  : []);
            if (data.engineers) setEngineers(Array.isArray(data.engineers)? data.engineers : []);
            if (data.indents)   setIndents(Array.isArray(data.indents)    ? data.indents   : []);
            if (data.labor)     setLabor(Array.isArray(data.labor)        ? data.labor     : []);
            setLiveStatus('live');
        }, []),
        onUpdate: useCallback((data) => {
            if (data.projects)  setProjects(Array.isArray(data.projects)  ? data.projects  : []);
            if (data.engineers) setEngineers(Array.isArray(data.engineers)? data.engineers : []);
            if (data.indents)   setIndents(Array.isArray(data.indents)    ? data.indents   : []);
            if (data.labor)     setLabor(Array.isArray(data.labor)        ? data.labor     : []);
        }, []),
    });

    const openModal = (type, item = null) => { setEditItem(item); setModal(type); };
    const closeModal = () => { setModal(null); setEditItem(null); };

    const sq = search.toLowerCase();

    // ── Computed values ────────────────────────────────────────
    const overdueInvoices = invoices.filter(i => i.status === 'Overdue' || (i.status === 'Unpaid' && i.due_date && new Date(i.due_date) < new Date()));
    const unpaidTotal     = invoices.filter(i => i.status === 'Unpaid' || i.status === 'Overdue').reduce((a, i) => a + parseFloat(i.total || 0), 0);
    const pendingExpenses = expenses.filter(e => e.status === 'Pending');

    // Vendor-wise spend from invoices
    const vendorSpend = useMemo(() => {
        const map = {};
        invoices.forEach(inv => {
            if (inv.status === 'Cancelled') return;
            if (!map[inv.vendor]) map[inv.vendor] = { vendor: inv.vendor, total: 0, paid: 0, count: 0 };
            map[inv.vendor].total += parseFloat(inv.total || 0);
            map[inv.vendor].count += 1;
            if (inv.status === 'Paid') map[inv.vendor].paid += parseFloat(inv.total || 0);
        });
        return Object.values(map).sort((a, b) => b.total - a.total);
    }, [invoices]);

    // Labour cost computed
    const laborCostData = useMemo(() => {
        const map = {};
        labor.forEach(l => {
            if (!map[l.site]) map[l.site] = { site: l.site, entries: 0, total_workers: 0, cost: 0 };
            map[l.site].entries += 1;
            map[l.site].total_workers += parseInt(l.total || 0);
            map[l.site].cost += parseFloat(l.total_cost || 0);
        });
        return Object.values(map);
    }, [labor]);

    // Budget utilization per project
    const budgetUtil = useMemo(() => {
        const map = {};
        budget.forEach(b => {
            if (!map[b.project]) map[b.project] = { project: b.project, allocated: 0, spent: 0, categories: [] };
            map[b.project].allocated += parseFloat(b.allocated || 0);
            map[b.project].spent     += parseFloat(b.spent || 0);
            map[b.project].categories.push(b);
        });
        return Object.values(map);
    }, [budget]);

    // ── Action handlers ────────────────────────────────────────
    const doSaveInvoice = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        setSaving(true);
        try {
            const body = {
                vendor:       f.get('vendor'),
                project:      f.get('project'),
                description:  f.get('description'),
                amount:       parseFloat(f.get('amount') || 0),
                tax:          parseFloat(f.get('tax') || 0),
                invoice_no:   f.get('invoice_no'),
                invoice_date: f.get('invoice_date'),
                due_date:     f.get('due_date'),
                category:     f.get('category'),
                status:       f.get('status') || 'Unpaid',
                indent_id:    f.get('indent_id') || null,
                notes:        f.get('notes'),
            };
            if (editItem?.id) {
                await api(`finance/invoices/${editItem.id}`, 'PUT', body);
                setInvoices(p => p.map(i => i.id === editItem.id ? { ...i, ...body } : i));
                showToast('Invoice updated.');
            } else {
                await api('finance/invoices', 'POST', body);
                const inv = await api('finance/invoices');
                setInvoices(Array.isArray(inv) ? inv : []);
                showToast('Invoice created.');
            }
            closeModal();
        } catch { showToast('Error saving invoice.'); }
        setSaving(false);
    };

    const doSavePayment = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        setSaving(true);
        try {
            await api('finance/payments', 'POST', {
                invoice_id: f.get('invoice_id') || null,
                vendor:     f.get('vendor'),
                project:    f.get('project'),
                amount:     parseFloat(f.get('amount') || 0),
                mode:       f.get('mode'),
                reference:  f.get('reference'),
                date:       f.get('date') || new Date().toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}),
                paid_by:    user?.name || '',
                notes:      f.get('notes'),
            });
            const [pay, inv] = await Promise.all([api('finance/payments'), api('finance/invoices')]);
            setPayments(Array.isArray(pay) ? pay : []);
            setInvoices(Array.isArray(inv) ? inv : []);
            closeModal();
            showToast('Payment recorded.');
        } catch { showToast('Error recording payment.'); }
        setSaving(false);
    };

    const doSaveExpense = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        setSaving(true);
        try {
            const body = {
                engineer:    f.get('engineer'),
                project:     f.get('project'),
                category:    f.get('category'),
                description: f.get('description'),
                amount:      parseFloat(f.get('amount') || 0),
                date:        f.get('date'),
                notes:       f.get('notes'),
            };
            if (editItem?.id) {
                await api(`finance/expenses/${editItem.id}`, 'PUT', body);
                setExpenses(p => p.map(x => x.id === editItem.id ? { ...x, ...body } : x));
            } else {
                await api('finance/expenses', 'POST', body);
                const exp = await api('finance/expenses');
                setExpenses(Array.isArray(exp) ? exp : []);
            }
            closeModal();
            showToast('Expense saved.');
        } catch { showToast('Error saving expense.'); }
        setSaving(false);
    };

    const doApproveExpense = async (id, status) => {
        try {
            await api(`finance/expenses/${id}`, 'PUT', { status, approved_by: user?.name });
            setExpenses(p => p.map(x => x.id === id ? { ...x, status } : x));
            showToast(`Expense ${status}.`);
        } catch { showToast('Error.'); }
    };

    const doDeleteInvoice = async (id) => {
        if (!window.confirm('Delete this invoice?')) return;
        try { await api(`finance/invoices/${id}`, 'DELETE'); setInvoices(p => p.filter(i => i.id !== id)); showToast('Deleted.'); }
        catch { showToast('Error.'); }
    };

    const doApproveInvoice = async (id) => {
        try {
            await api(`finance/invoices/${id}`, 'PUT', { status: 'Unpaid' });
            setInvoices(p => p.map(i => i.id === id ? { ...i, status: 'Unpaid' } : i));
            showToast('Bill approved — ready for payment.');
        } catch { showToast('Error approving.'); }
    };
    const doRejectInvoice = async (id) => {
        if (!window.confirm('Reject this bill? The engineer will need to re-submit.')) return;
        try {
            await api(`finance/invoices/${id}`, 'PUT', { status: 'Rejected' });
            setInvoices(p => p.map(i => i.id === id ? { ...i, status: 'Rejected' } : i));
            showToast('Bill rejected.');
        } catch { showToast('Error rejecting.'); }
    };

    const doDeleteExpense = async (id) => {
        if (!window.confirm('Delete this expense?')) return;
        try { await api(`finance/expenses/${id}`, 'DELETE'); setExpenses(p => p.filter(i => i.id !== id)); showToast('Deleted.'); }
        catch { showToast('Error.'); }
    };

    const doDeletePayment = async (id) => {
        if (!window.confirm('Delete this payment record?')) return;
        try { await api(`finance/payments/${id}`, 'DELETE'); setPayments(p => p.filter(i => i.id !== id)); showToast('Deleted.'); }
        catch { showToast('Error.'); }
    };

    const doSaveBudget = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        const projName = f.get('project');
        const proj = projects.find(p => p.name === projName);
        setSaving(true);
        try {
            await api('finance/budget', 'POST', {
                project_id: proj?.id || 0,
                project:    projName,
                category:   f.get('category'),
                allocated:  parseFloat(f.get('allocated') || 0),
                notes:      f.get('notes'),
            });
            const bud = await api('finance/budget');
            setBudget(Array.isArray(bud) ? bud : []);
            closeModal();
            showToast('Budget allocation saved.');
        } catch { showToast('Error.'); }
        setSaving(false);
    };

    const doUpdateLaborCost = async (e) => {
        e.preventDefault();
        const f = new FormData(e.target);
        setSaving(true);
        try {
            // Update all labor logs for this site with daily rates
            const skillRate   = parseFloat(f.get('skilled_rate')   || 0);
            const unskillRate = parseFloat(f.get('unskilled_rate') || 0);
            const site        = f.get('site');
            await api('labor', 'POST', {
                site,
                contractor:   f.get('contractor'),
                skilled:      parseInt(f.get('skilled') || 0),
                unskilled:    parseInt(f.get('unskilled') || 0),
                daily_rate_skilled:   skillRate,
                daily_rate_unskilled: unskillRate,
                total_cost:   (parseInt(f.get('skilled') || 0) * skillRate) + (parseInt(f.get('unskilled') || 0) * unskillRate),
            });
            const lab = await api('labor');
            setLabor(Array.isArray(lab) ? lab : []);
            closeModal();
            showToast('Labour cost logged.');
        } catch { showToast('Error.'); }
        setSaving(false);
    };

    if (loading) return (
        <>
        <style>{S}</style>
        <div className="fin-root">
            <div className="loading" style={{flex:1}}><RefreshCw size={22} className="spin" color="#1743e6"/> Loading finance data…</div>
        </div>
        </>
    );

    return (
        <>
        <style>{S}</style>
        <div className="fin-root">

            {/* Sidebar */}
            <aside className="fin-sb">
                <div className="fin-logo">
                    <div className="fin-logo-ico"><IndianRupee size={16} color="#fff"/></div>
                    <div>
                        <div className="fin-logo-txt">Finance Hub</div>
                        <div className="fin-logo-sub">MR Constructions</div>
                    </div>
                </div>

                <div className="fin-nav-sec">Navigation</div>
                {NAV.map(n => (
                    <button key={n.id} className={`fin-ni ${tab===n.id?'active':''}`}
                        onClick={() => { setTab(n.id); setSearch(''); }}>
                        <n.icon size={13}/> {n.id}
                        {n.id === 'Invoices'  && overdueInvoices.length > 0 && <span className="badge">{overdueInvoices.length}</span>}
                        {n.id === 'Expenses'  && pendingExpenses.length  > 0 && <span className="badge">{pendingExpenses.length}</span>}
                    </button>
                ))}

                <div className="fin-user">
                    <div className="fin-av">{user?.name?.split(' ').map(n=>n[0]).join('').slice(0,2) || 'FN'}</div>
                    <div>
                        <div className="fin-uname">{user?.name || 'Finance'}</div>
                        <div className="fin-urole">Finance Team</div>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="fin-main">

                {/* Header */}
                <div className="fin-hdr">
                    <div>
                        <h1 className="fin-ttl">{tab}</h1>
                        <p className="fin-sub">MR Constructions · Financial Control</p>
                    </div>
                    <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                        {tab !== 'Dashboard' && (
                            <div className="sw">
                                <Search size={13}/>
                                <input className="si" placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>
                            </div>
                        )}
                        <button onClick={fetchAll} style={{background:'var(--card)',border:'1px solid var(--border)',padding:'8px 10px',borderRadius:'8px',cursor:'pointer',display:'flex',alignItems:'center',boxShadow:'var(--sh)'}}>
                            <RefreshCw size={13} color="#94a3b8"/>
                        </button>
                        {tab==='Invoices'  && <button className="btn btn-primary" onClick={()=>openModal('invoice')}><Plus size={13}/> New Invoice</button>}
                        {tab==='Payments'  && <button className="btn btn-primary" onClick={()=>openModal('payment')}><Plus size={13}/> Record Payment</button>}
                        {tab==='Expenses'  && <button className="btn btn-primary" onClick={()=>openModal('expense')}><Plus size={13}/> Add Expense</button>}
                        {tab==='Budget'    && <button className="btn btn-primary" onClick={()=>openModal('budget')}><Plus size={13}/> Allocate Budget</button>}
                        {tab==='Labour Cost' && <button className="btn btn-primary" onClick={()=>openModal('labour')}><Plus size={13}/> Log Labour Cost</button>}
                    </div>
                </div>

                {/* ── DASHBOARD ── */}
                {tab === 'Dashboard' && summary && (<>
                    {overdueInvoices.length > 0 && (
                        <div className="alert-banner ab-red">
                            <AlertTriangle size={15} style={{flexShrink:0,marginTop:1}}/>
                            <strong>{overdueInvoices.length} overdue invoice{overdueInvoices.length>1?'s':''}</strong> — total ₹{unpaidTotal.toLocaleString()} outstanding. Review immediately.
                        </div>
                    )}
                    {pendingExpenses.length > 0 && (
                        <div className="alert-banner ab-warn">
                            <AlertCircle size={15} style={{flexShrink:0,marginTop:1}}/>
                            <strong>{pendingExpenses.length} engineer expense{pendingExpenses.length>1?'s':''}</strong> pending approval — ₹{pendingExpenses.reduce((a,e)=>a+parseFloat(e.amount||0),0).toLocaleString()} total.
                        </div>
                    )}

                    {/* KPIs */}
                    <div className="kpi-grid">
                        <div className="kpi" style={{'--kpi-color':'#1743e6'}} onClick={()=>setTab('Invoices')}>
                            <div className="kpi-ico" style={{background:'#eef3ff'}}><FileText size={18} color="#1743e6"/></div>
                            <div className="kpi-lbl">Total Invoiced</div>
                            <div className="kpi-val">{fmt(summary.total_invoiced)}</div>
                            <div className="kpi-sub">{invoices.length} invoices</div>
                        </div>
                        <div className="kpi" style={{'--kpi-color':'#10b981'}} onClick={()=>setTab('Payments')}>
                            <div className="kpi-ico" style={{background:'#f0fdf4'}}><CreditCard size={18} color="#10b981"/></div>
                            <div className="kpi-val" style={{color:'#10b981'}}>{fmt(summary.total_paid)}</div>
                            <div className="kpi-lbl">Total Paid</div>
                            <div className="kpi-sub">{payments.length} payments</div>
                        </div>
                        <div className="kpi" style={{'--kpi-color':unpaidTotal>0?'#ef4444':'#10b981'}} onClick={()=>setTab('Invoices')}>
                            <div className="kpi-ico" style={{background:unpaidTotal>0?'#fef2f2':'#f0fdf4'}}><AlertCircle size={18} color={unpaidTotal>0?'#ef4444':'#10b981'}/></div>
                            <div className="kpi-lbl">Outstanding</div>
                            <div className="kpi-val" style={{color:unpaidTotal>0?'#ef4444':'#10b981'}}>{fmt(unpaidTotal)}</div>
                            <div className="kpi-sub">{invoices.filter(i=>i.status==='Unpaid'||i.status==='Overdue').length} unpaid invoices</div>
                        </div>
                        <div className="kpi" style={{'--kpi-color':'#f59e0b'}} onClick={()=>setTab('Expenses')}>
                            <div className="kpi-ico" style={{background:'#fffbeb'}}><Receipt size={18} color="#f59e0b"/></div>
                            <div className="kpi-lbl">Field Expenses</div>
                            <div className="kpi-val">{fmt(summary.total_expenses)}</div>
                            <div className="kpi-sub">{pendingExpenses.length} pending approval</div>
                        </div>
                    </div>

                    <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'16px',marginBottom:'18px'}}>
                        {/* Monthly trend */}
                        <div className="card">
                            <div className="card-hdr"><span className="card-ttl">Monthly Invoiced vs Paid</span></div>
                            {(summary.monthly_trend||[]).length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <AreaChart data={summary.monthly_trend} margin={{top:8,right:8,left:-10,bottom:0}}>
                                        <defs>
                                            <linearGradient id="gInv" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%"  stopColor="#1743e6" stopOpacity={0.2}/>
                                                <stop offset="95%" stopColor="#1743e6" stopOpacity={0}/>
                                            </linearGradient>
                                            <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2}/>
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false}/>
                                        <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v=>fmt(v)}/>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                                        <Tooltip formatter={v=>fmt(v)} contentStyle={{borderRadius:'8px',border:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}/>
                                        <Area type="monotone" dataKey="invoiced" stroke="#1743e6" fill="url(#gInv)" name="Invoiced"/>
                                        <Area type="monotone" dataKey="paid"     stroke="#10b981" fill="url(#gPaid)" name="Paid"/>
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="empty"><BarChart3 size={32}/><div>No invoice data yet</div></div>
                            )}
                        </div>

                        {/* By category pie */}
                        <div className="card">
                            <div className="card-hdr"><span className="card-ttl">Spend by Category</span></div>
                            {(summary.by_category||[]).length > 0 ? (
                                <>
                                <ResponsiveContainer width="100%" height={150}>
                                    <PieChart>
                                        <Pie data={summary.by_category} dataKey="total" nameKey="category" innerRadius={40} outerRadius={65} paddingAngle={3}>
                                            {(summary.by_category||[]).map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                                        </Pie>
                                        <Tooltip formatter={v=>fmt(v)} contentStyle={{borderRadius:'8px',border:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}/>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{display:'flex',flexDirection:'column',gap:'5px',marginTop:'8px'}}>
                                    {(summary.by_category||[]).map((c,i)=>(
                                        <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:'12px',alignItems:'center'}}>
                                            <span style={{display:'flex',alignItems:'center',gap:'6px'}}>
                                                <span style={{width:'8px',height:'8px',borderRadius:'2px',background:PIE_COLORS[i%PIE_COLORS.length],flexShrink:0,display:'inline-block'}}/>
                                                {c.category}
                                            </span>
                                            <strong>{fmt(c.total)}</strong>
                                        </div>
                                    ))}
                                </div>
                                </>
                            ) : <div className="empty"><PieIcon size={28}/><div>No data</div></div>}
                        </div>
                    </div>

                    {/* Vendor spend table */}
                    <div className="card" style={{marginBottom:'16px'}}>
                        <div className="card-hdr">
                            <span className="card-ttl">Top Vendor Spend</span>
                            <button className="btn btn-ghost" style={{fontSize:'11px'}} onClick={()=>setTab('Vendor Costs')}>View All →</button>
                        </div>
                        <table className="ftbl">
                            <thead><tr><th>Vendor</th><th>Invoices</th><th>Total Invoiced</th><th>Paid</th><th>Outstanding</th><th>Utilisation</th></tr></thead>
                            <tbody>
                                {vendorSpend.slice(0,6).map(v=>(
                                    <tr key={v.vendor}>
                                        <td style={{fontWeight:'700'}}>{v.vendor}</td>
                                        <td>{v.count}</td>
                                        <td style={{fontWeight:'700'}}>{fmt(v.total)}</td>
                                        <td style={{color:'#16a34a',fontWeight:'600'}}>{fmt(v.paid)}</td>
                                        <td style={{color:v.total-v.paid>0?'#dc2626':'#16a34a',fontWeight:'600'}}>{fmt(v.total-v.paid)}</td>
                                        <td style={{width:'120px'}}>
                                            <div className="pb"><div className="pf" style={{width:`${v.total>0?Math.round((v.paid/v.total)*100):0}%`,background:'#10b981'}}/></div>
                                            <div style={{fontSize:'10px',color:'#94a3b8',marginTop:'2px'}}>{v.total>0?Math.round((v.paid/v.total)*100):0}% paid</div>
                                        </td>
                                    </tr>
                                ))}
                                {vendorSpend.length === 0 && <tr><td colSpan="6" style={{textAlign:'center',padding:'32px',color:'#94a3b8'}}>No vendor invoices yet.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* Engineer expenses */}
                    {(summary.expenses_by_project||[]).length > 0 && (
                        <div className="card">
                            <div className="card-hdr">
                                <span className="card-ttl">Engineer Expense Summary</span>
                                <button className="btn btn-ghost" style={{fontSize:'11px'}} onClick={()=>setTab('Expenses')}>View All →</button>
                            </div>
                            <table className="ftbl">
                                <thead><tr><th>Engineer</th><th>Project</th><th>Expenses</th><th>Total Amount</th></tr></thead>
                                <tbody>
                                    {(summary.expenses_by_project||[]).slice(0,8).map((e,i)=>(
                                        <tr key={i}>
                                            <td style={{fontWeight:'700'}}>{e.engineer}</td>
                                            <td style={{color:'#64748b'}}>{e.project}</td>
                                            <td>{e.count}</td>
                                            <td style={{fontWeight:'800',color:'#1743e6'}}>{fmt(e.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>)}

                {/* ── INVOICES ── */}
                {tab === 'Invoices' && (
                    <div className="card">
                        <table className="ftbl">
                            <thead><tr><th>Invoice ID</th><th>Vendor</th><th>Project</th><th>Category</th><th>Amount</th><th>Tax</th><th>Total</th><th>Due Date</th><th>Status</th><th>Actions</th></tr></thead>
                            <tbody>
                                {invoices.filter(i=>i.vendor?.toLowerCase().includes(sq)||i.project?.toLowerCase().includes(sq)||i.id?.toLowerCase().includes(sq)).map(inv=>(
                                    <tr key={inv.id} className="cr">
                                        <td style={{fontWeight:'800',color:'#1743e6',fontFamily:'monospace'}}>{inv.id}</td>
                                        <td style={{fontWeight:'700'}}>
                                            {inv.vendor||<span style={{color:'#94a3b8',fontWeight:500}}>—</span>}
                                            {(inv.uploaded_by||inv.created_by)&&<div style={{fontSize:'10px',color:'#94a3b8',fontWeight:500}}>by {inv.uploaded_by||inv.created_by}</div>}
                                        </td>
                                        <td style={{color:'#64748b',fontSize:'12px'}}>{inv.project}</td>
                                        <td><span style={{background:'#f1f5f9',padding:'2px 7px',borderRadius:'4px',fontSize:'11px',fontWeight:'600'}}>{inv.category}</span></td>
                                        <td>₹{parseFloat(inv.amount||0).toLocaleString()}</td>
                                        <td style={{color:'#94a3b8'}}>₹{parseFloat(inv.tax||0).toLocaleString()}</td>
                                        <td style={{fontWeight:'800'}}>{fmt(inv.total)}</td>
                                        <td style={{fontSize:'11px',color:inv.status==='Overdue'?'#dc2626':'#94a3b8'}}>{inv.due_date||'—'}</td>
                                        <td><span className={`badge b-${(inv.status||'unpaid').toLowerCase().replace(' ','-')}`}>{inv.status}</span></td>
                                        <td>
                                            <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                                                {inv.file_url&&<a href={inv.file_url} target="_blank" rel="noopener noreferrer" title="View attached invoice file" style={{display:'inline-flex',alignItems:'center',gap:'3px',color:'#1743e6',fontSize:'10px',fontWeight:'700',textDecoration:'none',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'5px',padding:'4px 7px'}}><FileText size={10}/> File</a>}
                                                <button title="Edit / complete details" style={{display:'inline-flex',alignItems:'center',gap:'3px',fontSize:'10px',fontWeight:'700',color:'#475569',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:'5px',padding:'4px 7px',cursor:'pointer',fontFamily:'inherit'}} onClick={()=>openModal('invoice',inv)}><Edit2 size={10}/> Edit</button>
                                                {inv.status==='Pending'&&<><button className="btn btn-green" style={{padding:'4px 8px',fontSize:'10px'}} onClick={()=>doApproveInvoice(inv.id)}><Check size={10}/> Approve</button>
                                                <button className="btn btn-red" style={{padding:'4px 8px',fontSize:'10px'}} onClick={()=>doRejectInvoice(inv.id)}><XCircle size={10}/> Reject</button></>}
                                                {inv.status==='Unpaid'&&<button className="btn btn-green" style={{padding:'4px 8px',fontSize:'10px'}} onClick={()=>openModal('payment',{invoice_id:inv.id,vendor:inv.vendor,project:inv.project,amount:inv.total})}><CreditCard size={10}/> Pay</button>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {invoices.length===0&&<tr><td colSpan="10"><div className="empty"><FileText size={32}/><div>No invoices yet. Click New Invoice to add.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── PAYMENTS ── */}
                {tab === 'Payments' && (
                    <div className="card">
                        <table className="ftbl">
                            <thead><tr><th>Payment ID</th><th>Vendor</th><th>Project</th><th>Amount</th><th>Mode</th><th>Reference</th><th>Date</th><th>Paid By</th><th>Actions</th></tr></thead>
                            <tbody>
                                {payments.filter(p=>p.vendor?.toLowerCase().includes(sq)||p.project?.toLowerCase().includes(sq)).map(pay=>(
                                    <tr key={pay.id}>
                                        <td style={{fontWeight:'800',color:'#10b981',fontFamily:'monospace'}}>{pay.id}</td>
                                        <td style={{fontWeight:'700'}}>{pay.vendor}</td>
                                        <td style={{color:'#64748b',fontSize:'12px'}}>{pay.project}</td>
                                        <td style={{fontWeight:'800',color:'#10b981'}}>{fmt(pay.amount)}</td>
                                        <td><span style={{background:'#f1f5f9',padding:'2px 7px',borderRadius:'4px',fontSize:'11px'}}>{pay.mode}</span></td>
                                        <td style={{fontSize:'11px',color:'#64748b',fontFamily:'monospace'}}>{pay.reference||'—'}</td>
                                        <td style={{fontSize:'11px',color:'#94a3b8'}}>{pay.date}</td>
                                        <td style={{fontSize:'11px'}}>{pay.paid_by}</td>
                                        <td></td>
                                    </tr>
                                ))}
                                {payments.length===0&&<tr><td colSpan="9"><div className="empty"><CreditCard size={32}/><div>No payments recorded yet.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── EXPENSES ── */}
                {tab === 'Expenses' && (
                    <div className="card">
                        <table className="ftbl">
                            <thead><tr><th>Exp ID</th><th>Engineer</th><th>Project</th><th>Category</th><th>Description</th><th>Amount</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
                            <tbody>
                                {expenses.filter(e=>e.engineer?.toLowerCase().includes(sq)||e.project?.toLowerCase().includes(sq)||e.description?.toLowerCase().includes(sq)).map(exp=>(
                                    <tr key={exp.id}>
                                        <td style={{fontWeight:'800',color:'#f59e0b',fontFamily:'monospace'}}>{exp.id}</td>
                                        <td style={{fontWeight:'700'}}>{exp.engineer}</td>
                                        <td style={{color:'#64748b',fontSize:'12px'}}>{exp.project}</td>
                                        <td><span style={{background:'#f1f5f9',padding:'2px 7px',borderRadius:'4px',fontSize:'11px'}}>{exp.category}</span></td>
                                        <td style={{maxWidth:'180px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#475569'}}>{exp.description}</td>
                                        <td style={{fontWeight:'800'}}>{fmt(exp.amount)}</td>
                                        <td style={{fontSize:'11px',color:'#94a3b8'}}>{exp.date}</td>
                                        <td><span className={`badge b-${(exp.status||'pending').toLowerCase()}`}>{exp.status}</span></td>
                                        <td style={{display:'flex',gap:'4px',flexWrap:'wrap',alignItems:'center'}}>
                                            {exp.receipt_url&&<a href={exp.receipt_url} target="_blank" rel="noopener noreferrer" title="View receipt / bill" style={{display:'inline-flex',alignItems:'center',gap:'3px',color:'#1743e6',fontSize:'10px',fontWeight:'700',textDecoration:'none',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'5px',padding:'4px 7px'}}><FileText size={10}/> Receipt</a>}
                                            {exp.status==='Pending'&&<>
                                                <button className="btn btn-green" style={{padding:'3px 7px',fontSize:'10px'}} onClick={()=>doApproveExpense(exp.id,'Approved')}><Check size={9}/> Approve</button>
                                                <button className="btn btn-red" style={{padding:'3px 7px',fontSize:'10px'}} onClick={()=>doApproveExpense(exp.id,'Rejected')}><XCircle size={9}/> Reject</button>
                                            </>}
                                        </td>
                                    </tr>
                                ))}
                                {expenses.length===0&&<tr><td colSpan="9"><div className="empty"><Receipt size={32}/><div>No expenses yet.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── BUDGET ── */}
                {tab === 'Budget' && (<>
                    {budgetUtil.length > 0 && (
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'14px',marginBottom:'20px'}}>
                            {budgetUtil.map((p,i)=>(
                                <div key={i} className="card">
                                    <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'4px'}}>{p.project}</div>
                                    <div style={{display:'flex',justifyContent:'space-between',fontSize:'12px',marginBottom:'6px'}}>
                                        <span style={{color:'#64748b'}}>Allocated: <strong>{fmt(p.allocated)}</strong></span>
                                        <span style={{color:'#64748b'}}>Spent: <strong style={{color:p.spent>p.allocated?'#dc2626':'#0f172a'}}>{fmt(p.spent)}</strong></span>
                                    </div>
                                    <div className="pb" style={{height:'7px'}}>
                                        <div className="pf" style={{width:`${p.allocated>0?Math.min(100,Math.round((p.spent/p.allocated)*100)):0}%`,background:p.spent>p.allocated?'#ef4444':'#1743e6'}}/>
                                    </div>
                                    <div style={{fontSize:'10px',color:'#94a3b8',marginTop:'4px'}}>{p.allocated>0?Math.round((p.spent/p.allocated)*100):0}% utilised</div>
                                    <div style={{marginTop:'10px',display:'flex',flexDirection:'column',gap:'4px'}}>
                                        {p.categories.map((c,j)=>(
                                            <div key={j} style={{display:'flex',justifyContent:'space-between',fontSize:'11px',padding:'4px 8px',background:'#f8fafc',borderRadius:'5px'}}>
                                                <span style={{color:'#64748b'}}>{c.category}</span>
                                                <strong>{fmt(c.allocated)}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="card">
                        <div className="card-hdr"><span className="card-ttl">Budget Allocations</span></div>
                        <table className="ftbl">
                            <thead><tr><th>Project</th><th>Category</th><th>Allocated</th><th>Notes</th></tr></thead>
                            <tbody>
                                {budget.filter(b=>b.project?.toLowerCase().includes(sq)).map((b,i)=>(
                                    <tr key={i}>
                                        <td style={{fontWeight:'700'}}>{b.project}</td>
                                        <td><span style={{background:'#f1f5f9',padding:'2px 7px',borderRadius:'4px',fontSize:'11px'}}>{b.category}</span></td>
                                        <td style={{fontWeight:'800',color:'#1743e6'}}>{fmt(b.allocated)}</td>
                                        <td style={{color:'#94a3b8',fontSize:'12px'}}>{b.notes||'—'}</td>
                                    </tr>
                                ))}
                                {budget.length===0&&<tr><td colSpan="4"><div className="empty"><Wallet size={32}/><div>No budget allocations. Click Allocate Budget.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>)}

                {/* ── VENDOR COSTS ── */}
                {tab === 'Vendor Costs' && (
                    <div className="card">
                        <div className="card-hdr"><span className="card-ttl">Vendor-wise Cost Breakdown</span></div>
                        <table className="ftbl">
                            <thead><tr><th>Vendor</th><th>Category</th><th>Invoices</th><th>Total Invoiced</th><th>Paid</th><th>Outstanding</th><th>Payment %</th></tr></thead>
                            <tbody>
                                {vendorSpend.filter(v=>v.vendor?.toLowerCase().includes(sq)).map((v,i)=>(
                                    <tr key={i}>
                                        <td style={{fontWeight:'800'}}>{v.vendor}</td>
                                        <td style={{fontSize:'11px',color:'#64748b'}}>
                                            {[...new Set(invoices.filter(inv=>inv.vendor===v.vendor).map(inv=>inv.category))].join(', ')}
                                        </td>
                                        <td>{v.count}</td>
                                        <td style={{fontWeight:'800'}}>{fmt(v.total)}</td>
                                        <td style={{color:'#16a34a',fontWeight:'700'}}>{fmt(v.paid)}</td>
                                        <td style={{color:v.total-v.paid>0?'#dc2626':'#16a34a',fontWeight:'700'}}>{fmt(v.total-v.paid)}</td>
                                        <td style={{width:'140px'}}>
                                            <div className="pb"><div className="pf" style={{width:`${v.total>0?Math.round((v.paid/v.total)*100):0}%`,background:'#10b981'}}/></div>
                                            <div style={{fontSize:'10px',color:'#94a3b8',marginTop:'2px'}}>{v.total>0?Math.round((v.paid/v.total)*100):0}%</div>
                                        </td>
                                    </tr>
                                ))}
                                {vendorSpend.length===0&&<tr><td colSpan="7"><div className="empty"><Building2 size={32}/><div>No vendor invoices yet.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── LABOUR COST ── */}
                {tab === 'Labour Cost' && (<>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'14px',marginBottom:'18px'}}>
                        {[
                            ['Total Entries', labor.length, '#1743e6'],
                            ['Total Workers', labor.reduce((a,l)=>a+parseInt(l.total||0),0), '#10b981'],
                            ['Total Cost', fmt(labor.reduce((a,l)=>a+parseFloat(l.total_cost||0),0)), '#f59e0b'],
                        ].map(([l,v,c])=>(
                            <div key={l} className="card" style={{borderTop:`3px solid ${c}`}}>
                                <div style={{fontSize:'10px',fontWeight:'700',color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'5px'}}>{l}</div>
                                <div style={{fontSize:'24px',fontWeight:'800',color:c}}>{v}</div>
                            </div>
                        ))}
                    </div>
                    <div className="card">
                        <table className="ftbl">
                            <thead><tr><th>Log ID</th><th>Date</th><th>Site</th><th>Contractor</th><th>Skilled</th><th>Unskilled</th><th>Total</th><th>Rate/Day (S)</th><th>Rate/Day (U)</th><th>Total Cost</th></tr></thead>
                            <tbody>
                                {labor.filter(l=>l.site?.toLowerCase().includes(sq)||l.contractor?.toLowerCase().includes(sq)).map(l=>(
                                    <tr key={l.id}>
                                        <td style={{fontWeight:'700',color:'#1743e6',fontFamily:'monospace'}}>{l.id}</td>
                                        <td style={{fontSize:'11px',color:'#94a3b8'}}>{l.date}</td>
                                        <td style={{fontWeight:'600'}}>{l.site}</td>
                                        <td style={{fontSize:'12px'}}>{l.contractor}</td>
                                        <td>{l.skilled}</td>
                                        <td>{l.unskilled}</td>
                                        <td style={{fontWeight:'700'}}>{l.total}</td>
                                        <td style={{color:'#64748b'}}>₹{l.daily_rate_skilled||'—'}</td>
                                        <td style={{color:'#64748b'}}>₹{l.daily_rate_unskilled||'—'}</td>
                                        <td style={{fontWeight:'800',color:parseFloat(l.total_cost||0)>0?'#1743e6':'#94a3b8'}}>{parseFloat(l.total_cost||0)>0?fmt(l.total_cost):'Not set'}</td>
                                    </tr>
                                ))}
                                {labor.length===0&&<tr><td colSpan="10"><div className="empty"><HardHat size={32}/><div>No labour logs yet.</div></div></td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>)}

                {/* ── FINANCE TEAM ── */}
                {tab === 'Finance Team' && (
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'14px'}}>
                        {finTeam.map(m=>(
                            <div key={m.id} className="card" style={{display:'flex',gap:'12px'}}>
                                <div style={{width:'48px',height:'48px',borderRadius:'12px',background:'linear-gradient(135deg,#0891b2,#06b6d4)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px',fontWeight:'800',flexShrink:0}}>
                                    {m.name?.split(' ').map(n=>n[0]).join('').slice(0,2)}
                                </div>
                                <div style={{flex:1}}>
                                    <div style={{fontWeight:'800',fontSize:'14px'}}>{m.name}</div>
                                    <div style={{fontSize:'11px',color:'#64748b',marginBottom:'4px'}}>{m.role}</div>
                                    <div style={{fontSize:'11px',color:'#0891b2'}}>{m.email}</div>
                                    <div style={{marginTop:'8px',paddingTop:'8px',borderTop:'1px solid #f8fafc',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                        <span style={{fontSize:'10px',color:m.status==='Active'?'#16a34a':'#94a3b8',fontWeight:'700'}}>● {m.status}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {finTeam.length===0&&<div className="card" style={{gridColumn:'1/-1'}}><div className="empty"><Users size={32}/><div>No finance team members added yet.</div></div></div>}
                    </div>
                )}

            </main>
        </div>

        {/* ═══════════════ MODALS ═══════════════ */}

        {/* New / Edit Invoice */}
        {modal === 'invoice' && (
            <div className="moverlay">
                <div className="mbox">
                    <div className="mhdr"><h2>{editItem?.id ? 'Edit Invoice' : 'Create Invoice'}</h2><button className="mcls" onClick={closeModal}><X size={16}/></button></div>
                    <div className="mbody">
                        <form onSubmit={doSaveInvoice}>
                            <div className="grid2">
                                <div className="fg"><label>Vendor *</label>
                                    <select name="vendor" required defaultValue={editItem?.vendor||''}>
                                        <option value="">-- Select Vendor --</option>
                                        {vendors.map(v=><option key={v.id} value={v.name}>{v.name}</option>)}
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="fg"><label>Project *</label>
                                    <select name="project" required defaultValue={editItem?.project||''}>
                                        <option value="">-- Select Project --</option>
                                        {projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="fg"><label>Description</label><input name="description" defaultValue={editItem?.description||''} placeholder="What this invoice is for"/></div>
                            <div className="grid3">
                                <div className="fg"><label>Amount (₹) *</label><input name="amount" type="number" min="0" step="0.01" defaultValue={editItem?.amount||''} required/></div>
                                <div className="fg"><label>Tax / GST (₹)</label><input name="tax" type="number" min="0" step="0.01" defaultValue={editItem?.tax||0}/></div>
                                <div className="fg"><label>Category</label>
                                    <select name="category" defaultValue={editItem?.category||'Material'}>
                                        {['Material','Labour','Equipment','Transport','Services','Other'].map(c=><option key={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid3">
                                <div className="fg"><label>Invoice No.</label><input name="invoice_no" defaultValue={editItem?.invoice_no||''} placeholder="INV-001"/></div>
                                <div className="fg"><label>Invoice Date</label><input name="invoice_date" defaultValue={editItem?.invoice_date||''} placeholder="01 Jan 2025"/></div>
                                <div className="fg"><label>Due Date</label><input name="due_date" defaultValue={editItem?.due_date||''} placeholder="15 Jan 2025"/></div>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Linked Indent</label>
                                    <select name="indent_id" defaultValue={editItem?.indent_id||''}>
                                        <option value="">-- None --</option>
                                        {indents.map(i=><option key={i.id} value={i.id}>{i.id} — {i.item}</option>)}
                                    </select>
                                </div>
                                {editItem?.id && (
                                    <div className="fg"><label>Status</label>
                                        <select name="status" defaultValue={editItem?.status||'Unpaid'}>
                                            {['Unpaid','Paid','Overdue','Partial','Cancelled'].map(s=><option key={s}>{s}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                            <div className="fg"><label>Notes</label><textarea name="notes" defaultValue={editItem?.notes||''} placeholder="Additional notes…"/></div>
                            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'11px'}} disabled={saving}>
                                {saving?'Saving…':<><FileText size={14}/> {editItem?.id?'Update Invoice':'Create Invoice'}</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {/* Record Payment */}
        {modal === 'payment' && (
            <div className="moverlay">
                <div className="mbox">
                    <div className="mhdr"><h2>Record Payment</h2><button className="mcls" onClick={closeModal}><X size={16}/></button></div>
                    <div className="mbody">
                        <form onSubmit={doSavePayment}>
                            <div className="grid2">
                                <div className="fg"><label>Vendor *</label>
                                    <select name="vendor" required defaultValue={editItem?.vendor||''}>
                                        <option value="">-- Select Vendor --</option>
                                        {vendors.map(v=><option key={v.id} value={v.name}>{v.name}</option>)}
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="fg"><label>Project *</label>
                                    <select name="project" required defaultValue={editItem?.project||''}>
                                        <option value="">-- Select Project --</option>
                                        {projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="fg"><label>Linked Invoice</label>
                                <select name="invoice_id" defaultValue={editItem?.invoice_id||''}>
                                    <option value="">-- None --</option>
                                    {invoices.filter(i=>i.status==='Unpaid'||i.status==='Overdue').map(i=>(
                                        <option key={i.id} value={i.id}>{i.id} — {i.vendor} — {fmt(i.total)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Amount Paid (₹) *</label><input name="amount" type="number" min="0" step="0.01" defaultValue={editItem?.amount||''} required/></div>
                                <div className="fg"><label>Payment Mode</label>
                                    <select name="mode">
                                        {['Bank Transfer','NEFT','RTGS','IMPS','Cheque','Cash','UPI'].map(m=><option key={m}>{m}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Reference / UTR No.</label><input name="reference" placeholder="e.g. UTR12345678"/></div>
                                <div className="fg"><label>Payment Date</label><input name="date" placeholder="01 Jan 2025"/></div>
                            </div>
                            <div className="fg"><label>Notes</label><textarea name="notes" placeholder="Remarks…"/></div>
                            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'11px',background:'#10b981'}} disabled={saving}>
                                {saving?'Recording…':<><CreditCard size={14}/> Record Payment</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {/* Add / Edit Expense */}
        {modal === 'expense' && (
            <div className="moverlay">
                <div className="mbox">
                    <div className="mhdr"><h2>{editItem?.id?'Edit Expense':'Add Engineer Expense'}</h2><button className="mcls" onClick={closeModal}><X size={16}/></button></div>
                    <div className="mbody">
                        <form onSubmit={doSaveExpense}>
                            <div className="grid2">
                                <div className="fg"><label>Engineer *</label>
                                    <select name="engineer" required defaultValue={editItem?.engineer||''}>
                                        <option value="">-- Select Engineer --</option>
                                        {engineers.map(e=><option key={e.id} value={e.name}>{e.name}</option>)}
                                    </select>
                                </div>
                                <div className="fg"><label>Project *</label>
                                    <select name="project" required defaultValue={editItem?.project||''}>
                                        <option value="">-- Select Project --</option>
                                        {projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Category</label>
                                    <select name="category" defaultValue={editItem?.category||'Travel'}>
                                        {['Travel','Accommodation','Food','Tools & Equipment','Site Materials','Communication','Miscellaneous'].map(c=><option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="fg"><label>Amount (₹) *</label><input name="amount" type="number" min="0" step="0.01" defaultValue={editItem?.amount||''} required/></div>
                            </div>
                            <div className="fg"><label>Description *</label><input name="description" defaultValue={editItem?.description||''} placeholder="What was this expense for?" required/></div>
                            <div className="fg"><label>Date</label><input name="date" defaultValue={editItem?.date||''} placeholder="01 Jan 2025"/></div>
                            <div className="fg"><label>Notes</label><textarea name="notes" defaultValue={editItem?.notes||''} placeholder="Additional details…"/></div>
                            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'11px'}} disabled={saving}>
                                {saving?'Saving…':<><Receipt size={14}/> {editItem?.id?'Update Expense':'Submit Expense'}</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {/* Budget Allocation */}
        {modal === 'budget' && (
            <div className="moverlay">
                <div className="mbox" style={{maxWidth:'460px'}}>
                    <div className="mhdr"><h2>Allocate Budget</h2><button className="mcls" onClick={closeModal}><X size={16}/></button></div>
                    <div className="mbody">
                        <form onSubmit={doSaveBudget}>
                            <div className="fg"><label>Project *</label>
                                <select name="project" required>
                                    <option value="">-- Select Project --</option>
                                    {projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Category</label>
                                    <select name="category">
                                        {['Material','Labour','Equipment','Transport','Overheads','Contingency','Other'].map(c=><option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="fg"><label>Allocated Amount (₹) *</label><input name="allocated" type="number" min="0" step="0.01" required/></div>
                            </div>
                            <div className="fg"><label>Notes</label><textarea name="notes" placeholder="Budget breakdown notes…"/></div>
                            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'11px'}} disabled={saving}>
                                {saving?'Saving…':<><Wallet size={14}/> Allocate Budget</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {/* Log Labour Cost */}
        {modal === 'labour' && (
            <div className="moverlay">
                <div className="mbox" style={{maxWidth:'480px'}}>
                    <div className="mhdr"><h2>Log Labour Cost</h2><button className="mcls" onClick={closeModal}><X size={16}/></button></div>
                    <div className="mbody">
                        <form onSubmit={doUpdateLaborCost}>
                            <div className="fg"><label>Site *</label>
                                <select name="site" required>
                                    <option value="">-- Select Site --</option>
                                    {projects.map(p=><option key={p.id} value={p.name}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="fg"><label>Contractor</label><input name="contractor" placeholder="Contractor / agency name"/></div>
                            <div className="grid2">
                                <div className="fg"><label>Skilled Workers</label><input name="skilled" type="number" min="0" defaultValue="0"/></div>
                                <div className="fg"><label>Unskilled Workers</label><input name="unskilled" type="number" min="0" defaultValue="0"/></div>
                            </div>
                            <div style={{background:'#eef3ff',border:'1px solid #c9d7ff',borderRadius:'9px',padding:'13px',marginBottom:'13px'}}>
                                <div style={{fontSize:'11px',fontWeight:'700',color:'#1743e6',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Daily Rates</div>
                                <div className="grid2">
                                    <div className="fg"><label>Skilled Rate (₹/day)</label><input name="skilled_rate" type="number" min="0" step="0.01" placeholder="e.g. 650"/></div>
                                    <div className="fg"><label>Unskilled Rate (₹/day)</label><input name="unskilled_rate" type="number" min="0" step="0.01" placeholder="e.g. 450"/></div>
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary" style={{width:'100%',padding:'11px'}} disabled={saving}>
                                {saving?'Saving…':<><HardHat size={14}/> Log Labour Cost</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}

        {/* Toast */}
        {toast && (
            <div style={{position:'fixed',bottom:'22px',right:'22px',background:'#0f172a',color:'#fff',padding:'12px 18px',borderRadius:'10px',boxShadow:'0 8px 24px rgba(0,0,0,0.28)',display:'flex',alignItems:'center',gap:'9px',zIndex:9999,fontWeight:'600',fontSize:'13px',borderLeft:'3px solid #10b981',fontFamily:'Plus Jakarta Sans,sans-serif',maxWidth:'340px'}}>
                <CheckCircle2 size={14} color="#10b981"/>{toast}
            </div>
        )}
        </>
    );
}